// ScenarioExportDialog — Phase 3: Export scenario in various formats
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as XlsxIcon,
  Code as JsonIcon,
} from '@mui/icons-material';
import { useExportScenario } from '../../../hooks/useBomIntelligence';
import { colors } from '../../../theme/analyticsColors';

interface ScenarioExportDialogProps {
  scenarioId: string;
  open: boolean;
  onClose: () => void;
}

const ScenarioExportDialog: React.FC<ScenarioExportDialogProps> = ({
  scenarioId,
  open,
  onClose,
}) => {
  const [format, setFormat] = useState<'PDF' | 'XLSX' | 'JSON'>('PDF');
  const exportMutation = useExportScenario();

  const handleExport = async () => {
    exportMutation.mutate(
      { scenarioId, format },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const ext = format === 'PDF' ? 'pdf' : format === 'JSON' ? 'json' : 'csv';
          a.download = `scenario-export.${ext}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
        Export Scenario
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.85rem', color: colors.secondary, mb: 2 }}>
          Choose the export format for this scenario.
        </Typography>

        <RadioGroup value={format} onChange={(e) => setFormat(e.target.value as any)}>
          <FormControlLabel
            value="PDF"
            control={<Radio size="small" />}
            label={
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.85rem' }}>
                <PdfIcon sx={{ fontSize: 18, color: colors.chart.error }} /> PDF Report
              </Typography>
            }
          />
          <FormControlLabel
            value="XLSX"
            control={<Radio size="small" />}
            label={
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.85rem' }}>
                <XlsxIcon sx={{ fontSize: 18, color: colors.chart.success }} /> Spreadsheet (CSV)
              </Typography>
            }
          />
          <FormControlLabel
            value="JSON"
            control={<Radio size="small" />}
            label={
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.85rem' }}>
                <JsonIcon sx={{ fontSize: 18, color: colors.chart.primary }} /> JSON Data
              </Typography>
            }
          />
        </RadioGroup>

        {exportMutation.isError && (
          <Alert severity="error" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Export failed. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: colors.secondary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleExport}
          disabled={exportMutation.isPending}
          startIcon={exportMutation.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {exportMutation.isPending ? 'Exporting...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioExportDialog;
